import React from 'react';
import { Message } from '@/shared/hooks/useMessages';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/avatarUtils';

interface MessageBubbleProps {
  message: Message;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ✓  = sent (not read)   ✓✓ = read
const ReadReceipt: React.FC<{ isRead: boolean }> = ({ isRead }) => (
  <span
    className={cn(
      'text-[10px] ml-1 select-none',
      isRead ? 'text-white/80' : 'text-white/40',
    )}
    aria-label={isRead ? 'Read' : 'Sent'}
  >
    {isRead ? '✓✓' : '✓'}
  </span>
);

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const initials = getInitials(message.sender_name);

  return (
    <div
      className={cn(
        'flex w-full mb-3 items-end gap-2',
        message.is_mine ? 'justify-end' : 'justify-start',
      )}
    >
      {/* Sender avatar — only for incoming messages */}
      {!message.is_mine && (
        <div className="flex-shrink-0 mb-1">
          <FramedAvatar
            src={message.sender_picture ?? undefined}
            initials={initials}
            beltRank={message.sender_belt_rank}
            username={message.sender_username ?? undefined}
            frameSize={{ w: 30, h: 34 }}
            isManual
          />
        </div>
      )}

      {/* Bubble */}
      <div
        className={cn(
          'max-w-[72%] px-4 py-2.5 rounded-2xl text-sm break-words',
          message.is_mine
            ? 'bg-[#C0392B] text-white rounded-br-sm'
            : 'bg-white/10 text-white/90 rounded-bl-sm',
        )}
      >
        <p className="leading-relaxed">{message.content}</p>
        <div className="flex items-center justify-end gap-0.5 mt-1">
          <span
            className={cn(
              'text-[10px]',
              message.is_mine ? 'text-white/60' : 'text-white/40',
            )}
          >
            {formatTime(message.created_at)}
          </span>
          {message.is_mine && <ReadReceipt isRead={message.is_read} />}
        </div>
      </div>
    </div>
  );
};
